import React, { useState, useEffect } from "react";
import { FaMobile, FaSync } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import { useDebounce } from "../../../../Hooks/useDebounce";
import { FirmDropdown } from "../../../atoms";
import usePermission from "../../../../Hooks/usePermission";
import {
  useLazyGetDealerRolesDeviceByFirmIDQuery,
  useLazyGetDealerRolesFirmProductsQuery,
  useGetDealerRolesFirmsQuery,
} from "../../../../store/dealerRoles/firmApi";
import { dealerHasSubDealers } from "../../../../utils/deviceUtils";
import {
  useDealerToggleDeviceActivityMutation,
  useDealerMoveDeviceToPlaymakerMutation,
  useDealerMoveDeviceToServiceMutation,
  useDealerMoveDeviceToFirmMutation,
  useDealerMoveDeviceToStorageMutation,
  useFetchDealerPlayMakerQuery,
  useDealerGiveDeviceQuotaMutation,
  useDealerTakeBackDeviceQuotaMutation,
} from "../../../../store/dealerRoles/devicesApi";
import DevicesTableSkeleton from "../DevicesTableSkeleton";
import DevicesTable from "../DevicesTable";
import DeviceOperationsDrawer from "../DeviceOperationsDrawer";
import OperationDrawer from "../../../Device/OperationDrawer";
import DeviceQuotaDrawer from "../DeviceQuotaDrawer";
import FirmBusyBadge from "../../FirmBusyBadge";
import useFirmBusy from "../../../../Hooks/dealerRoles/useFirmBusy";
import { EmptyState, MascotLoadingOverlay } from "../../../shared";
import { Alert } from "@mui/material";
import { DEVICE_ACTIVITY_DEFAULT_NOTE_CLOSE, DEVICE_ACTIVITY_DEFAULT_NOTE_OPEN } from "../../../../utils/deviceActivityLabels";

const DealerRolesDevicesTab = ({ firm }) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const [filterSerialNo, setFilterSerialNo] = useState("");
  const [filterEyeCount, setFilterEyeCount] = useState("");
  const debouncedSerialNo = useDebounce(filterSerialNo, 250);
  const debouncedEyeCount = useDebounce(filterEyeCount, 250);
  // Alt firma süzgeci. VARSAYILAN: yalnızca firmanın KENDİSİ; alt firmalar
  // seçiciden eklenir (depo tarafıyla aynı kural).
  const [filterFirmIds, setFilterFirmIds] = useState(null);
  const [filterModelName, setFilterModelName] = useState("");
  const [devices, setDevices] = useState([]);
  const [alert, setAlert] = useState(null);

  // OperationDrawer state'leri (Aç/Kapat, Plasiyere Ver, Servise Al,
  // Alt Firmaya / Alt Bayiye Ver, Bayiye Geri Al — #247)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDevice, setDrawerDevice] = useState(null);
  const [drawerOperation, setDrawerOperation] = useState(null);
  const [drawerFirmTarget, setDrawerFirmTarget] = useState("customer");

  // Manuel Kota Yükle drawer state'i

  // Operasyonlar drawer'ı — tablo satırındaki buton açar; deviceId
  // ile güncel liste objesine bağlanır.
  const [operationsDrawer, setOperationsDrawer] = useState({ open: false, deviceId: null, view: null });

  // Cihaz bazında kota ver/al drawer'ı
  const [quotaDrawer, setQuotaDrawer] = useState({ open: false, deviceId: null, direction: "give" });

  const [
    getDeviceByFirmID,
    {
      data: devicesData,
      isLoading: deviceIsLoading,
      isFetching: deviceIsFetching,
      isError: deviceIsError,
    },
  ] = useLazyGetDealerRolesDeviceByFirmIDQuery();

  // Ürün kotaları: getFirmProducts ürün başına TOPLAM kotayı döner
  // (firma bakiyesi + cihazlardaki kota). Bakiye burada çıkarılarak bulunur.
  // getFirmSync BİLEREK kullanılmıyor — o uç ürün fiyatı/katsayısı gibi ayar
  // alanlarını da döndürüyor ve ürün ayarları iznine bağlı.
  const [getFirmProducts, { data: firmProductsData, isFetching: firmProductsFetching }] =
    useLazyGetDealerRolesFirmProductsQuery();

  const [giveDeviceQuota, giveQuotaStatus] = useDealerGiveDeviceQuotaMutation();
  const [takeBackDeviceQuota, takeBackQuotaStatus] = useDealerTakeBackDeviceQuotaMutation();

  const [moveDeviceToService, serviceMoveStatus] = useDealerMoveDeviceToServiceMutation();
  const [moveDeviceToFirm, firmMoveStatus] = useDealerMoveDeviceToFirmMutation();
  const [moveDeviceToStorage, storageMoveStatus] = useDealerMoveDeviceToStorageMutation();
  // Alt Firmaya / Alt Bayiye Ver hedef listesi: bayi ağı (kendisi + alt
  // bayiler + müşteriler). Yetki yoksa liste boş gelir, aksiyon da gizlenir.
  const { data: dealerFirmsData } = useGetDealerRolesFirmsQuery(
    { token: auth.accessToken },
    { skip: !auth?.accessToken },
  );
  const dealerFirms = dealerFirmsData?.data?.firms || [];
  const hasSubDealers = dealerHasSubDealers(auth, dealerFirms);
  const [moveDeviceToPlaymaker, playmakerMoveStatus] = useDealerMoveDeviceToPlaymakerMutation();
  const [toggleDeviceActivity, resultToggleDeviceActivity] = useDealerToggleDeviceActivityMutation();
  // Uç devices.moveToPlaymaker ister — izni yoksa istek atılmaz (#414)
  const { data: playmakerData } = useFetchDealerPlayMakerQuery(auth?.accessToken, {
    skip: !hasPermission("devices.moveToPlaymaker"),
  });

  useEffect(() => {
    if (firm?._id && auth?.accessToken) {
      getDeviceByFirmID({ id: firm._id, token: auth.accessToken });
    }
  }, []);

  useEffect(() => {
    if (devicesData?.data) {
      setDevices(devicesData.data.devices);
    }
  }, [devicesData?.data]);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Kota bakiyesi kartları KENDİ iznine bağlı — ürün ayarları sekmesi izniyle
  // (dealers/customers.productSettings) karıştırılmaz; o izin fiyat/katsayı
  // düzenlemeyi de açıyor.
  const canSeeQuota = hasPermission("devices.quotaBalance");

  // Kota transferi yalnızca BAYİ firmasındaki (kendi firması veya alt bayi)
  // cihazlarda yapılır; müşteri firmasındaki cihazda menüde hiç görünmez
  // (sunucu da 400 döner).
  const isDealerOwnedFirm = firm?.firmType === "dealer";
  const canTransferQuota =
    isDealerOwnedFirm &&
    (hasPermission("devices.giveQuota") || hasPermission("devices.takeBackQuota"));

  useEffect(() => {
    if (firm?._id && auth?.accessToken && canSeeQuota) {
      getFirmProducts({ id: firm._id, token: auth.accessToken });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm?._id, auth?.accessToken, canSeeQuota]);

  const refreshDevices = () => {
    if (firm?._id && auth?.accessToken) {
      getDeviceByFirmID({ id: firm._id, token: auth.accessToken });
      if (canSeeQuota) getFirmProducts({ id: firm._id, token: auth.accessToken });
    }
  };

  // ─── Cihaz bazında kota ver/al ───────────────────────────────────────────
  // Firma bakiyesi ile tek cihaz arasında transfer; toplam kota değişmez.
  const quotaDrawerDevice =
    devices.find((d) => d._id === quotaDrawer.deviceId) || null;

  // getFirmProducts: [{ productName, quota }] — quota TOPLAM (bakiye + firmanın
  // KENDİ "Firma" durumundaki cihazları; bkz. Server getDealerFirmProducts).
  // Bakiye = toplam − o cihazlardaki. Liste alt firma cihazlarını da getirdiği
  // için yalnız kök firmanın cihazları düşülür (önceden hepsi düşülüyor, alt
  // firma cihazı varken bakiye eksik çıkıyordu).
  const rootFirmId = firm?._id ? String(firm._id) : "";
  const deviceFirmId = (d) => String(d.firm?._id || d.firm || "");
  const deviceProductQuota = (list, productName) =>
    list.reduce((sum, d) => {
      const item = d.productInfo?.productInfo?.find(
        (item) => item.productName === productName,
      );
      return sum + (Number(item?.quota) || 0);
    }, 0);
  // Liste zaten yalnız "Firma" durumundaki cihazları döndürür.
  const ownDevices = devices.filter((d) => deviceFirmId(d) === rootFirmId);
  const firmQuotas = (firmProductsData?.data || []).map((p) => {
    const total = Number(p.quota) || 0;
    return {
      productName: p.productName,
      total,
      balance: total - deviceProductQuota(ownDevices, p.productName),
    };
  });

  // Drawer'ın beklediği biçim: ProductFirm.productInfo benzeri { productName, quota }
  // — burada quota = firma bakiyesi.
  const firmProductInfoes = firmQuotas.map((c) => ({
    productName: c.productName,
    quota: c.balance,
  }));

  const handleQuotaSubmit = ({ productName, quota, note }) => {
    const payload = {
      token: auth.accessToken,
      deviceId: quotaDrawer.deviceId,
      productName,
      quota,
      note: note || "",
    };
    if (quotaDrawer.direction === "give") giveDeviceQuota(payload);
    else takeBackDeviceQuota(payload);
  };

  useEffect(() => {
    if (giveQuotaStatus.isSuccess) {
      setAlert({ severity: "success", message: "Kota cihaza verildi." });
      setQuotaDrawer({ open: false, deviceId: null, direction: "give" });
      refreshDevices();
    }
    if (giveQuotaStatus.isError) {
      setAlert({
        severity: "error",
        message: giveQuotaStatus.error?.data?.message || "Kota verme işlemi başarısız.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giveQuotaStatus.isSuccess, giveQuotaStatus.isError]);

  useEffect(() => {
    if (takeBackQuotaStatus.isSuccess) {
      setAlert({ severity: "success", message: "Kota firma bakiyesine alındı." });
      setQuotaDrawer({ open: false, deviceId: null, direction: "give" });
      refreshDevices();
    }
    if (takeBackQuotaStatus.isError) {
      setAlert({
        severity: "error",
        message: takeBackQuotaStatus.error?.data?.message || "Kota alma işlemi başarısız.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeBackQuotaStatus.isSuccess, takeBackQuotaStatus.isError]);

  // Servise alma sonucu
  useEffect(() => {
    if (serviceMoveStatus.isSuccess) {
      setAlert({ severity: "success", message: "Cihaz başarıyla servise alındı." });
      refreshDevices();
    }
    if (serviceMoveStatus.isError) {
      setAlert({ severity: "error", message: serviceMoveStatus.error?.data?.message || "Servise alırken bir hata oluştu." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceMoveStatus.isSuccess, serviceMoveStatus.isError]);

  // Alt firmaya / alt bayiye verme ve bayiye geri alma sonucu (#247)
  useEffect(() => {
    if (firmMoveStatus.isSuccess) {
      setAlert({ severity: "success", message: "Cihaz başarıyla verildi." });
      refreshDevices();
    }
    if (firmMoveStatus.isError) {
      setAlert({ severity: "error", message: firmMoveStatus.error?.data?.message || "İşlem sırasında bir hata oluştu." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmMoveStatus.isSuccess, firmMoveStatus.isError]);

  useEffect(() => {
    if (storageMoveStatus.isSuccess) {
      setAlert({ severity: "success", message: "Cihaz başarıyla deponuza alındı." });
      refreshDevices();
    }
    if (storageMoveStatus.isError) {
      setAlert({ severity: "error", message: storageMoveStatus.error?.data?.message || "İşlem sırasında bir hata oluştu." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageMoveStatus.isSuccess, storageMoveStatus.isError]);

  // Cihazı kota almaya açma/kapama sonucu
  useEffect(() => {
    if (resultToggleDeviceActivity.isSuccess) {
      setAlert({ severity: "success", message: "Kota alma durumu güncellendi." });
      refreshDevices();
    }
    if (resultToggleDeviceActivity.isError) {
      setAlert({ severity: "error", message: resultToggleDeviceActivity.error?.data?.message || "İşlem sırasında bir hata oluştu." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultToggleDeviceActivity.isSuccess, resultToggleDeviceActivity.isError]);

  // Plasiyere taşıma sonucu
  useEffect(() => {
    if (playmakerMoveStatus.isSuccess) {
      setAlert({ severity: "success", message: "Cihaz başarıyla plasiyere verildi." });
      refreshDevices();
    }
    if (playmakerMoveStatus.isError) {
      setAlert({ severity: "error", message: playmakerMoveStatus.error?.data?.message || "İşlem sırasında bir hata oluştu." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playmakerMoveStatus.isSuccess, playmakerMoveStatus.isError]);

  const handleRefresh = () => refreshDevices();

  // OperationDrawer handler fonksiyonları
  // Firması meşgulse (kilit tutuluyor, #303) cihaz işlemi başlatılmaz —
  // sunucu da reddeder; kullanıcı formu doldurup boşa beklemesin.
  const blockIfBusy = (device) => {
    const id = String(device?.firm?._id || device?.firm || "");
    if (!isBusy(id)) return false;
    setAlert({
      severity: "warning",
      message: "Bu firmada bir işlem sürüyor. Bitince tekrar deneyin.",
    });
    return true;
  };

  const handleOpenOperationDrawer = (device, operation, opts = {}) => {
    if (blockIfBusy(device)) return;
    setDrawerDevice(device);
    setDrawerOperation(operation);
    setDrawerFirmTarget(opts.firmTarget || "customer");
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerDevice(null);
    setDrawerOperation(null);
    firmMoveStatus.reset();
    storageMoveStatus.reset();
  };

  const handleDrawerConfirm = (operationData) => {
    const { device, connect, description, selectedPlaymakerId: playmakerId } = operationData;
    const token = auth.accessToken;

    if (drawerOperation === "Servis") {
      moveDeviceToService({ id: device._id, connect, description, token, isActive: operationData.isActive ?? true });
    } else if (drawerOperation === "Firma") {
      if (!operationData.firmName) return;
      moveDeviceToFirm({
        token,
        id: device._id,
        connect,
        firmName: operationData.firmName,
        description: description || "",
        isActive: operationData.isActive || false,
      });
    } else if (drawerOperation === "Depo") {
      moveDeviceToStorage({ token, id: device._id, connect, description });
    } else if (drawerOperation === "AktiflikKapat") {
      toggleDeviceActivity({
        token,
        id: device._id,
        connect,
        description: description || DEVICE_ACTIVITY_DEFAULT_NOTE_CLOSE,
        isActive: false,
      });
    } else if (drawerOperation === "AktiflikAc") {
      toggleDeviceActivity({
        token,
        id: device._id,
        connect,
        description: description || DEVICE_ACTIVITY_DEFAULT_NOTE_OPEN,
        isActive: true,
      });
    } else if (drawerOperation === "Plasiyer") {
      if (!playmakerId) return;
      moveDeviceToPlaymaker({
        token,
        id: device._id,
        connect,
        userID: playmakerId,
        description: description || "Cihaz plasiyere verildi.",
      });
    }
  };

  // Seri no, göz sayısı ve model ismine göre filtreleme
  // Listedeki cihazların ait olduğu firmalar (alt firma cihazları da gelir).
  const firmOptions = (() => {
    const map = new Map();
    // firmType seçicide tip ikonunu belirler — kök bayi ise liste bayinin
    // müşterilerini de içerir (#200).
    if (firm?._id) {
      map.set(String(firm._id), {
        _id: String(firm._id),
        firmName: firm.firmName,
        firmType: firm.firmType,
      });
    }
    devices.forEach((d) => {
      const id = String(d.firm?._id || d.firm || "");
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          _id: id,
          firmName: d.firm?.firmName || "-",
          firmType: d.firm?.firmType,
        });
      }
    });
    return [...map.values()];
  })();
  const hasSubFirms = firmOptions.length > 1;
  // Firma meşguliyeti (#303) — listedeki firmalardan kilidi tutulanlar.
  const { isBusy } = useFirmBusy(firmOptions.map((f) => f._id));
  const rootBusy = isBusy(firm?._id);
  // Etkin kapsam — dokunulmadıysa yalnız kök firma.
  const effectiveFirmIds =
    filterFirmIds && filterFirmIds.length > 0
      ? filterFirmIds
      : firm?._id
        ? [String(firm._id)]
        : [];
  const selectedFirms = firmOptions.filter((f) =>
    effectiveFirmIds.includes(f._id)
  );

  // Kota kartları firma seçicinin KAPSAMINI izler (#300): alt firmalar
  // seçilince o firmaların cihazlarındaki kota da Toplam'a eklenir.
  // Toplam = (kök firma seçiliyse bakiye) + seçili firmaların cihazları.
  // Bakiye her zaman kök firmanın havuzudur — kota transferi oradan yapılır.
  // Seri no / model / göz filtreleri kartı etkilemez.
  const scopeDevices = hasSubFirms
    ? devices.filter((d) => effectiveFirmIds.includes(deviceFirmId(d)))
    : devices;
  const rootInScope = !hasSubFirms || effectiveFirmIds.includes(rootFirmId);
  const quotaCards = firmQuotas.map((q) => ({
    ...q,
    total:
      (rootInScope ? q.balance : 0) +
      deviceProductQuota(scopeDevices, q.productName),
  }));

  const displayDevices = (() => {
    // Süzgeçler gecikmeli (#407): her tuşta tüm kartlar yeniden çizilmesin.
    const sn = (debouncedSerialNo || "").trim().toLowerCase();
    const eye = (debouncedEyeCount || "").trim();
    const model = (filterModelName || "").trim().toLowerCase();
    const firmFilterActive = hasSubFirms && effectiveFirmIds.length > 0;
    if (!sn && !eye && !model && !firmFilterActive) return devices;
    return devices.filter((d) => {
      if (firmFilterActive) {
        const id = String(d.firm?._id || d.firm || "");
        if (!effectiveFirmIds.includes(id)) return false;
      }
      if (sn && !(d.serialNo || "").toLowerCase().includes(sn)) return false;
      if (model && !(d.deviceName || "").toLowerCase().includes(model)) return false;
      if (eye) {
        const num = parseInt(eye, 10);
        const cup = d.deviceInfo?.cupNumber ?? d.cupNumber;
        if (!Number.isNaN(num)) {
          if (cup === undefined || cup === null || Number(cup) !== num) return false;
        } else if (!String(cup ?? "").includes(eye)) return false;
      }
      return true;
    });
  })();

  const hasNoData = !deviceIsLoading && !deviceIsFetching && displayDevices.length === 0;

  // Drawer'daki cihaz — refresh sonrası bayat obje kalmasın diye listeden çözülür
  const operationsDrawerDevice =
    devices.find((d) => d._id === operationsDrawer.deviceId) || null;

  return (
    <div className="flex flex-col h-full w-full">
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "12px", fontSize: "13px" }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shrink-0 px-2 sm:px-4 py-3 sm:py-4 space-y-2.5">
        <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-lg shrink-0">
              <FaMobile className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-800 leading-tight">
                Cihazlar
                {rootBusy && <FirmBusyBadge className="ml-2 align-middle" />}
              </h2>
              <p className="text-[11px] sm:text-xs text-gray-500">
                {displayDevices.length} cihaz
                {(filterSerialNo ||
                  filterEyeCount ||
                  filterModelName ||
                  effectiveFirmIds.length !== firmOptions.length) &&
                  ` (${devices.length} toplam)`}
              </p>
            </div>
          </div>

          {/* Ürün kota bakiyeleri — depo tarafındaki Cihazlar sekmesiyle aynı
              okuma: Toplam = bakiye + cihazlardaki kota, Bakiye = firmada
              duran (dağıtılmamış) kota. Transferler bu bakiyeden yapılır. */}
          {canSeeQuota && (firmProductsFetching || quotaCards.length > 0) && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar min-w-0 -mx-1 px-1 order-last sm:order-none w-full sm:w-auto">
              {firmProductsFetching && quotaCards.length === 0
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="shrink-0 bg-gray-50 rounded-xl px-4 py-2.5 animate-pulse">
                      <div className="h-3 w-16 bg-gray-200 rounded mb-2" />
                      <div className="h-5 w-24 bg-gray-200 rounded" />
                    </div>
                  ))
                : quotaCards.map((card, index) => {
                    const palette = [
                      { bg: "bg-blue-50 border-blue-200", label: "text-blue-500", value: "text-blue-700", soft: "text-blue-400" },
                      { bg: "bg-emerald-50 border-emerald-200", label: "text-emerald-500", value: "text-emerald-700", soft: "text-emerald-400" },
                      { bg: "bg-amber-50 border-amber-200", label: "text-amber-500", value: "text-amber-700", soft: "text-amber-400" },
                      { bg: "bg-indigo-50 border-indigo-200", label: "text-indigo-500", value: "text-indigo-700", soft: "text-indigo-400" },
                      { bg: "bg-pink-50 border-pink-200", label: "text-pink-500", value: "text-pink-700", soft: "text-pink-400" },
                    ];
                    const color = palette[index % palette.length];
                    return (
                      <div
                        key={card.productName}
                        className={`shrink-0 ${color.bg} border rounded-xl px-4 py-2.5`}
                      >
                        <p className={`text-xs font-bold ${color.label} whitespace-nowrap leading-none mb-1.5`}>
                          {card.productName}
                        </p>
                        <div className="flex items-baseline gap-2">
                          <div className="text-center">
                            <p className={`text-lg font-Bold ${color.value} leading-none tabular-nums`}>
                              {card.total.toLocaleString("tr-TR")}
                            </p>
                            <p className="text-[9px] font-medium text-gray-400 mt-0.5">Toplam Kota</p>
                          </div>
                          <span className="text-gray-300 font-light text-lg leading-none">|</span>
                          <div className="text-center">
                            <p className={`text-lg font-Bold leading-none tabular-nums ${card.balance < 0 ? "text-red-500" : color.soft}`}>
                              {card.balance.toLocaleString("tr-TR")}
                            </p>
                            <p className="text-[9px] font-medium text-gray-400 mt-0.5">Bakiye</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
            </div>
          )}

          {/* Seri No, Model İsmi, Göz Sayısı filtreleri + Yenile butonu — tek grup, sağa yaslı */}
          <div className="flex items-center gap-2.5 w-full sm:w-auto min-w-0 flex-wrap sm:flex-nowrap">
            {/* Alt firma süzgeci — yalnızca alt firma cihazı varsa görünür */}
            {hasSubFirms && (
              <div className="w-40 sm:w-44 md:w-52 shrink-0">
                <FirmDropdown
                  compact
                  multiple
                  firms={firmOptions}
                  value={selectedFirms}
                  placeholder="Firma seçin"
                  onChange={(next) => {
                    const ids = (next || []).map((f) => f._id);
                    setFilterFirmIds(
                      ids.length > 0 ? ids : firm?._id ? [String(firm._id)] : [],
                    );
                  }}
                />
              </div>
            )}
            <input
              type="text"
              placeholder="Seri No"
              value={filterSerialNo}
              onChange={(e) => setFilterSerialNo(e.target.value)}
              className="h-9 flex-1 sm:flex-initial min-w-0 sm:w-36 md:w-40 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              placeholder="Model İsmi"
              value={filterModelName}
              onChange={(e) => setFilterModelName(e.target.value)}
              className="h-9 flex-1 sm:flex-initial min-w-0 sm:w-36 md:w-40 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="Göz sayısı"
              value={filterEyeCount}
              onChange={(e) => setFilterEyeCount(e.target.value.replace(/\D/g, "").slice(0, 2))}
              className="h-9 w-20 sm:w-24 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              onClick={handleRefresh}
              disabled={deviceIsLoading || deviceIsFetching}
              className="h-9 flex items-center justify-center gap-1.5 px-3 bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap disabled:opacity-40 shrink-0"
            >
              <FaSync className={`w-3.5 h-3.5 shrink-0 ${deviceIsLoading || deviceIsFetching ? "animate-spin" : ""}`} />
              <span>Yenile</span>
            </button>
          </div>
        </div>
      </div>

      {/* İçerik — tablo görünümü kenar boşluksuz, sabit başlıkla kendi
          içinde kayar; loading/empty durumları eski padding'li düzende */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {deviceIsLoading || deviceIsFetching ? (
          <div className="relative flex-1 min-h-0">
            <MascotLoadingOverlay message="Cihazlar yükleniyor..." />
            <DevicesTableSkeleton rows={6} />
          </div>
        ) : deviceIsError || devices.length === 0 || hasNoData ? (
          <div className="flex-1 overflow-y-auto min-h-0 px-2 sm:px-4 py-3">
            <EmptyState
              message={filterSerialNo || filterEyeCount || filterModelName ? "Sonuç bulunamadı" : "Kayıtlı Cihaz Bulunamadı"}
              suggestion={filterSerialNo || filterEyeCount || filterModelName ? "Filtreleri değiştirerek tekrar deneyin." : "Bu firmaya ait kayıtlı cihaz bulunmamaktadır."}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <DevicesTable
              devices={displayDevices}
              firm={firm}
              onOpenOperations={(device, view) =>
                setOperationsDrawer({ open: true, deviceId: device._id, view })
              }
              onOpenOperationDrawer={handleOpenOperationDrawer}
              hasSubDealers={hasSubDealers}
              onOpenQuotaDrawer={
                canTransferQuota
                  ? (device, direction) =>
                      !blockIfBusy(device) &&
                      setQuotaDrawer({ open: true, deviceId: device._id, direction })
                  : undefined
              }
            />
          </div>
        )}
      </div>

      {/* Operasyonlar Drawer — tablo satırından açılır */}
      <DeviceOperationsDrawer
        open={operationsDrawer.open}
        onClose={() => setOperationsDrawer({ open: false, deviceId: null, view: null })}
        device={operationsDrawerDevice}
        initialView={operationsDrawer.view}
        onOpenOperationDrawer={handleOpenOperationDrawer}
      />

      {/* İşlem Drawer — Aç/Kapat, Plasiyere Ver, Servise Al, Alt Firmaya /
          Alt Bayiye Ver, Bayiye Geri Al */}
      <OperationDrawer
        open={drawerOpen}
        device={drawerDevice}
        operation={drawerOperation}
        onClose={handleDrawerClose}
        onConfirm={handleDrawerConfirm}
        playmakers={playmakerData?.data?.playmakers || []}
        firms={dealerFirms}
        firmTarget={drawerFirmTarget}
        isLoading={
          serviceMoveStatus.isLoading ||
          resultToggleDeviceActivity.isLoading ||
          playmakerMoveStatus.isLoading ||
          firmMoveStatus.isLoading ||
          storageMoveStatus.isLoading
        }
        isSuccess={
          (drawerOperation === "Firma" && firmMoveStatus.isSuccess) ||
          (drawerOperation === "Depo" && storageMoveStatus.isSuccess) ||
          (drawerOperation === "Servis" && serviceMoveStatus.isSuccess) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && resultToggleDeviceActivity.isSuccess) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isSuccess)
        }
        isError={
          (drawerOperation === "Firma" && firmMoveStatus.isError) ||
          (drawerOperation === "Depo" && storageMoveStatus.isError) ||
          (drawerOperation === "Servis" && serviceMoveStatus.isError) ||
          ((drawerOperation === "AktiflikKapat" || drawerOperation === "AktiflikAc") && resultToggleDeviceActivity.isError) ||
          (drawerOperation === "Plasiyer" && playmakerMoveStatus.isError)
        }
        errorMessage={
          firmMoveStatus.error?.data?.message ||
          storageMoveStatus.error?.data?.message ||
          serviceMoveStatus.error?.data?.message ||
          resultToggleDeviceActivity.error?.data?.message ||
          playmakerMoveStatus.error?.data?.message ||
          ""
        }
      />

      {/* Ayarlar Drawer (kısıtlı/whitelist'li) */}
      {/* Cihaz bazında kota ver/al */}
      <DeviceQuotaDrawer
        open={quotaDrawer.open}
        onClose={() => setQuotaDrawer({ open: false, deviceId: null, direction: "give" })}
        onSubmit={handleQuotaSubmit}
        direction={quotaDrawer.direction}
        device={quotaDrawerDevice}
        firmProducts={firmProductInfoes}
        isLoading={giveQuotaStatus.isLoading || takeBackQuotaStatus.isLoading}
      />
    </div>
  );
};

export default DealerRolesDevicesTab;
